import { useBreakpointValue } from '@genial/utils';
import React, { useState } from 'react';
import { useRecoilValue } from 'recoil';

import useSignedInNavigation from '../../../../hooks/useSignedInNavigation';
import { useCustomerActions } from '../../../../store/customer/actions';
import { isExportingPdfForCustomers } from '../../../../store/customer/atom';
import {
  CustomerAccountV3Normalized,
  CustomerV4FinancialResponseNormalized,
} from '../../../../store/customer/types';

import { CustomerTableItemDesktop } from './desktop';
import { CustomerTableItemMobile } from './mobile';
import { CustomerTableItemTablet } from './tablet';

export namespace CustomerTableItemTypes {
  export interface ReceiveProps {
    customer: CustomerV4FinancialResponseNormalized;
  }

  export interface Props extends ReceiveProps {
    onDownloadIncomeStatementFile: (customer: CustomerV4FinancialResponseNormalized) => void;
    customerDetailNavigation: () => void;
    customerDetailHref: string;
    isExporting?: boolean;
    accountSelected?: CustomerAccountV3Normalized;
    setAccountSelected: (accountId: string) => void;
  }
}

export const CustomerTableItem: React.FC<CustomerTableItemTypes.ReceiveProps> = (props) => {
  const { customer } = props;
  const [accountSelected, setAccountSelected] = useState<CustomerAccountV3Normalized>(
    props.customer?.accountsNormalized[0]
  );

  const { downloadIncomeStatementFile } = useCustomerActions();
  const isExportingPdfData = useRecoilValue(isExportingPdfForCustomers);

  const handleDownloadIncomeStatement = (
    currentCustomer: CustomerV4FinancialResponseNormalized
  ) => {
    if (!currentCustomer.cpfCnpjText) {
      return;
    }
    downloadIncomeStatementFile(
      currentCustomer.cpfCnpjText,
      currentCustomer.currentYearIncomeStatement
    );
  };

  const { href: customerDetailHref, navigate: customerDetailNavigation } = useSignedInNavigation(
    'CustomerDetail',
    {
      customerId: customer.customerId,
      accountId: accountSelected.accountId,
      accountNumber: accountSelected.accountNumber,
      cpfCnpj: customer.cpfCnpjText || '',
      cpfCnpjFormatted: customer.cpfCnpjFormatted || '',
    }
  );

  const handleSelectAccount = (sinacorAccountNormalized: string) => {
    const account = props.customer.accountsNormalized.find(
      (acc) => acc.sinacorAccountNormalized === sinacorAccountNormalized
    );
    if (!account) {
      return;
    }
    setAccountSelected(account);
  };

  const data = {
    ...props,
    customerDetailNavigation,
    customerDetailHref,
    onDownloadIncomeStatementFile: handleDownloadIncomeStatement,
    isExporting: isExportingPdfData,
    accountSelected,
    setAccountSelected: handleSelectAccount,
  };

  const customerTableItem = useBreakpointValue({
    base: <CustomerTableItemMobile {...data} />,
    sm: <CustomerTableItemTablet {...data} />,
    lg: <CustomerTableItemDesktop {...data} />,
  });

  return customerTableItem;
};
